import { useAppSelector } from "@app/hooks"
import GenericDialog from "@cmp/genericDialog"
import { selectLatestPublishedCoursesByIds } from "@features/coursesMenu/coursesSelectors"
import {
  selectCourseIdsWithInstances,
  selectCourseInstanceByIdentifiers,
  selectCourseInstanceClasses,
} from "@features/courseInstance/courseInstanceSelectors"
import { selectLoggedInUser } from "@features/login/loginSelectors"
import { selectTermsByFilter } from "@features/term/termSelector"
import { Autocomplete, Stack, TextField, Typography } from "@mui/material"
import { formatDateAsYearAndLongMonth } from "@utils/date"
import { useState } from "react"
import { uuidToShortId } from "../../../main/courses-table/utils"

type StudentCourseClassPopupModel = {
  studentId: string
  firstName: string
  lastName: string
}

type CourseClassRegistrationPopupProps = {
  model: StudentCourseClassPopupModel | undefined
  title: string | JSX.Element
  confirmText: string
  onClose: () => void
  onConfirm: (args: {
    courseClassId: string
    studentId: string
    userClientId: string
  }) => Promise<void>
}

function CourseClassRegistrationPopup(
  props: CourseClassRegistrationPopupProps,
) {
  const { model, title, confirmText, onClose, onConfirm } = props
  const [selectedCourse, setSelectedCourse] = useState<any | null>(null)
  const [selectedTerm, setSelectedTerm] = useState<any | null>(null)
  const [selectedClass, setSelectedClass] = useState<any | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const user = useAppSelector(selectLoggedInUser)
  const justBeforeWeStartUsingTerms = new Date("2024-11-01")
  const terms = useAppSelector((state) =>
    selectTermsByFilter(state, justBeforeWeStartUsingTerms.toISOString(), 25),
  )
  const courseIds = useAppSelector(selectCourseIdsWithInstances)
  const courses = useAppSelector((state) =>
    selectLatestPublishedCoursesByIds(state, courseIds),
  )
  const courseInstance = useAppSelector((state) =>
    selectCourseInstanceByIdentifiers(
      state,
      selectedCourse?.id || "",
      selectedTerm?.id || "",
      undefined,
    ),
  )
  const classes = useAppSelector((state) =>
    selectCourseInstanceClasses(state, courseInstance?.id),
  )

  const onConfirmClick = () => {
    if (!user || !model || !selectedClass) return
    setIsLoading(true)

    onConfirm({
      courseClassId: selectedClass.id,
      studentId: model.studentId,
      userClientId: user.clientId,
    })
      .then(() => {
        setSelectedCourse(null)
        setSelectedTerm(null)
        setSelectedClass(null)
        onClose()
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  if (!model) return null

  return (
    <GenericDialog
      open
      size="xs"
      onClose={onClose}
      title={title}
      content={
        <Stack direction="column" sx={{ pt: "16px", gap: "24px" }}>
          <Autocomplete
            fullWidth
            options={courses}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedCourse}
            onChange={(_, newValue) => {
              setSelectedCourse(newValue)
              setSelectedTerm(null)
              setSelectedClass(null)
            }}
            renderInput={(params) => <TextField {...params} label="Course" />}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack
                  direction="row"
                  sx={{
                    width: "100%",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                  }}
                >
                  <Stack>
                    <Typography variant="body2">{option.title}</Typography>
                    <Typography variant="body3" color="text.secondary">
                      {option.name}
                    </Typography>
                  </Stack>
                  <Typography variant="body3" color="text.disabled">
                    {option.displayId || uuidToShortId(option.id)}
                  </Typography>
                </Stack>
              </li>
            )}
          />
          <Autocomplete
            fullWidth
            options={terms}
            getOptionLabel={(option) =>
              formatDateAsYearAndLongMonth(option.startDate)
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedTerm}
            onChange={(_, newValue) => {
              setSelectedTerm(newValue)
              setSelectedClass(null)
            }}
            disabled={!selectedCourse}
            renderInput={(params) => <TextField {...params} label="Term" />}
          />
          <Autocomplete
            fullWidth
            options={classes}
            getOptionLabel={(option) => option.language}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedClass}
            onChange={(_, newValue) => setSelectedClass(newValue)}
            disabled={!selectedCourse || !selectedTerm}
            renderInput={(params) => <TextField {...params} label="Class" />}
          />
        </Stack>
      }
      buttons={[
        {
          type: "primary",
          text: confirmText,
          disabled: !user || !model || !selectedClass,
          loading: isLoading,
          onClick: onConfirmClick,
        },
      ]}
    />
  )
}

export default CourseClassRegistrationPopup
